'use client';

import { forwardRef } from 'react';
import { SystemStyleObject, chakra, LinkProps as ChakraLinkProps, StyleProps } from '@chakra-ui/react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { Url } from 'next/dist/shared/lib/router/router';

import Icon, { IconKind, IconName } from '@/components/atomic/v1/icon/Icon';

const hoverStyles: SystemStyleObject = {
  color: 'button-primary/blue:hover',
  textDecoration: 'underline'
};
const focusStyles: SystemStyleObject = {
  ...hoverStyles,
  outline: '2px solid',
  outlineColor: 'button-primary/blue:hover',
  borderRadius: '2px',
  boxShadow: 'none'
};

// This defines the base CSS styles for all `Link` components
const baseStyleProps: SystemStyleObject = {
  textUnderlineOffset: '4px',
  color: 'button-primary/blue:default',
  fontSize: 'regular',
  display: 'inline-flex',
  alignItems: 'center',
  columnGap: 2,
  _hover: hoverStyles,
  _focus: focusStyles
};

export interface CustomLinkProps extends Omit<NextLinkProps, 'as' | 'href'> {
  href?: NextLinkProps['href'];
  children?: React.ReactNode;
  icon?: IconName | undefined;
  iconPlacement?: 'right' | 'left' | undefined;
  isExternal?: boolean; // opens the link in a new tab
  iconKind?: IconKind;
  overrideExternalIcon?: boolean;
  nextAs?: NextLinkProps['as'];
  sx?: StyleProps;
}

const CustomLink = forwardRef<HTMLAnchorElement, CustomLinkProps>(
  (
    {
      children,
      href,
      icon,
      isExternal = false,
      iconKind = 'outline',
      iconPlacement = 'right',
      overrideExternalIcon = false,
      nextAs,
      ...rest
    },
    ref
  ) => {
    const isIconVisible = icon || (isExternal && !overrideExternalIcon); // No need to pass icon prop for an external link; there will be a default icon that will be used

    // External links will default to having a specific icon, regardless of what icon is passed to the icon prop, unless overridden
    const LinkIcon = isIconVisible ? (
      <Icon
        name={isExternal && !overrideExternalIcon ? 'arrow-top-right' : (icon as IconName)}
        size={(isExternal && !overrideExternalIcon) || icon === 'arrow-right' ? 18 : 24}
        minWidth={(isExternal && !overrideExternalIcon) || icon === 'arrow-right' ? '18px' : '24px'}
        kind={iconKind}
      />
    ) : null;

    if (href === undefined) {
      return (
        <chakra.a ref={ref} cursor="pointer" {...rest}>
          {iconPlacement === 'left' && LinkIcon}
          {children}
          {iconPlacement === 'right' && LinkIcon}
        </chakra.a>
      );
    }

    return (
      <NextLink ref={ref} href={href} target={isExternal ? '_blank' : undefined} as={nextAs as Url} {...rest}>
        {iconPlacement === 'left' && LinkIcon}
        {children}
        {iconPlacement === 'right' && LinkIcon}
      </NextLink>
    );
  }
);

type LinkProps = CustomLinkProps & Omit<ChakraLinkProps, 'children' | 'href'>;

// using the chakra factory function due to the `as` prop conflict between NextLink and ChakraLink
const Link: React.FC<LinkProps> = chakra<typeof CustomLink, CustomLinkProps>(CustomLink, {
  baseStyle: baseStyleProps
});

export default Link;
